/**
 * Plan Card Component
 * Individual card component for displaying plan information
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 9.1, 9.2, 9.3, 10.1, 10.2, 11.1, 11.3
 */

import { Component, EventEmitter, Input, Output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AccessPlan } from '../../../../core/generated/model/accessPlan';
import { AccessPlanControllerService, ProductPackageControllerService } from '../../../../core/generated';
import { fadeIn } from '../../shared/animations';
import { UpgradeOptionsModalComponent } from '../upgrade-options-modal/upgrade-options-modal.component';
import { PlanUpgradeConfirmationComponent } from '../plan-upgrade-confirmation/plan-upgrade-confirmation.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-plan-card',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatMenuModule
  ],
  templateUrl: './plan-card.component.html',
  styleUrls: ['./plan-card.component.scss'],
  animations: [fadeIn]
})
export class PlanCardComponent {
  @Input() plan!: AccessPlan;
  @Output() viewDetails = new EventEmitter<AccessPlan>();
  @Output() viewPayments = new EventEmitter<AccessPlan>();

  private accessPlanService = inject(AccessPlanControllerService);
  private packageService = inject(ProductPackageControllerService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  isUpdatingAutoRenew = signal(false);

  /**
   * Toggle auto renew directly from the card
   */
  onToggleAutoRenew(event: Event): void {
    event.stopPropagation();
    if (!this.plan.id || this.isUpdatingAutoRenew()) return;

    const newValue = !this.plan.autoRenew;
    this.isUpdatingAutoRenew.set(true);

    this.accessPlanService.updateAutoRenew(this.plan.id, newValue).subscribe({
      next: (updatedPlan) => {
        this.plan.autoRenew = updatedPlan.autoRenew;
        const msg = newValue ? 'Renovação automática ativada!' : 'Renovação automática desativada!';
        this.snackBar.open(msg, 'Fechar', { duration: 3000 });
        this.isUpdatingAutoRenew.set(false);
      },
      error: () => {
        this.snackBar.open('Erro ao atualizar renovação automática.', 'Fechar', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isUpdatingAutoRenew.set(false);
      }
    });
  }

  /**
   * Calculate monthly value from plan's final total
   * Requirement 3.5: Display monthly value
   */
  getMonthlyValue(): number {
    return 0;
  }

  /**
   * Get status color for mat-chip
   * Requirement 9.2: Status badge color mapping
   */
  getStatusColor(active?: boolean): 'accent' | 'warn' {
    return active ? 'accent' : 'warn';
  }

  /**
   * Get status label text
   * Requirement 9.1: Status badge display
   */
  getStatusLabel(active?: boolean): string {
    return active ? 'Ativo' : 'Inativo';
  }

  /**
   * Format date to Brazilian format (DD/MM/YYYY)
   * Requirement 3.1: Display creation date
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  }

  /**
   * Format currency to Brazilian format (R$ X.XXX,XX)
   * Requirement 3.5: Display financial summary
   */
  formatCurrency(value?: number): string {
    if (value === undefined || value === null) return 'R$ 0,00';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Emit event to view plan details
   * Requirement 10.1: Action buttons
   */
  onViewDetails(): void {
    this.viewDetails.emit(this.plan);
  }

  /**
   * Emit event to view plan payments
   * Requirement 10.2: Navigate to payments
   */
  onViewPayments(): void {
    this.viewPayments.emit(this.plan);
  }

  /**
   * Open upgrade options modal
   * Requirements: 2.1, 2.2, 8.1, 8.2, 8.3, 8.4
   */
  openUpgradeOptions(): void {
    if (!this.plan?.id) {
      this.snackBar.open('Plano inválido', 'Fechar', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(UpgradeOptionsModalComponent, {
      data: { planId: this.plan.id },
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'upgrade-options-dialog',
      disableClose: false,
      autoFocus: true
    });
    console.log('Opened UpgradeOptionsModalComponent for planId:', this.plan.id);
    dialogRef.afterClosed().subscribe((selection?: { planId: number; packageId: number }) => {
      console.log('Upgrade selection:', selection);
      if (selection) {
        this.handleUpgradeSelection(selection);
      }
    });
  }

  /**
   * Handle upgrade selection
   * Requirements: 8.3, 8.4
   */
  private async handleUpgradeSelection(selection: { planId: number; packageId: number }): Promise<void> {
    try {
      const targetPackage = await firstValueFrom(
        this.packageService.findById10(selection.packageId)
      );

      this.dialog.open(PlanUpgradeConfirmationComponent, {
        data: { currentPlan: this.plan, targetPackage },
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'upgrade-confirmation-dialog',
        disableClose: false
      });
    } catch {
      this.snackBar.open('Erro ao carregar detalhes do pacote de upgrade.', 'Fechar', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
    }
  }
}
